<script setup lang="ts">
import type { CalculatedPrice } from "#shared/types/pricing";

const props = defineProps<{
  variant: PacketVariant;
}>();

const auth = useAuthorizeStore();

const fixed = computed(() => !!props.variant.fixedAmount);

const totStudents = useTotStudents();

const prices = usePrices(props.variant);

const label = computed(() =>
  auth.isConsumer
    ? "Lisensen varer i ett år."
    : fixed.value
      ? "Elevantall blir lagt til i handlekurven"
      : "Antall legger du til i handlekurven",
);

const key = computed(() => (auth.isConsumer ? "incVat" : "exVat") satisfies keyof CalculatedPrice);

const { data: settings, suspense } = useSettings();

await suspense();

const disclaimer = computed(() => {
  const raw = auth.isConsumer
    ? settings.value?.discountDisclaimer?.consumer ?? "Gjelder kun første skoleår. Deretter gjelder vanlig/ordinær pris."
    : settings.value?.discountDisclaimer?.organization ?? "Gjelder ut inneværende skoleår. Abonnementet starter 1. juli og gjelder for neste skoleår.";

  return interpolate(raw, {
    originalPriceExVat: prices.value === "NOT_APPLICABLE" ? "" : norwegianPrice(prices.value.original.exVat),
    originalPriceIncVat: prices.value === "NOT_APPLICABLE" ? "" : norwegianPrice(prices.value.original.incVat),
    recurringStartDate: settings.value?.discountDisclaimer?.recurringStartDate?.toLocaleDateString("nb-NO", {
      month: "long",
      day: "numeric",
    }) ?? "",
  });
});
</script>

<template>
  <div v-if="prices !== 'NOT_APPLICABLE'" class="flex flex-col gap-s">
    <div class="flex flex-col gap-2xs">
      <div class="flex items-end justify-between">
        <h5
          class="text-lg font-semibold md:text-2xl"
          v-text="
            auth.isConsumer
              ? 'For privatpersoner'
              : fixed
                ? 'Til hele skolen'
                : 'Enkelttilganger'
          "
        />
        <IconsDiscount
          :value="prices.discounted?.percentage"
          size="small"
          class="[--background:theme(colors.coral.20)] [--color:theme(colors.black.80)] [--stroke:transparent]"
          :class="{
            invisible: !prices.discounted || prices.discounted.exVat === 0,
          }"
        />
      </div>
      <p class="text-base" v-text="label" />
    </div>
    <div class="flex items-center justify-between">
      <div class="flex flex-col gap-2xs">
        <template v-if="prices.discounted">
          <del
            aria-label="Ordinær pris per tilgang"
            class="text-base text-gray-40"
            v-text="norwegianPrice(prices.original[key])"
          />
          <PacketVariantCardActionPrice
            is="ins"
            :price="prices.discounted"
            :is-metered="prices.actual.isMetered"
            aria-label="Rabattert pris per tilgang"
            class="text-red-30 no-underline"
            is-discounted
          />
        </template>
        <template v-else>
          <PacketVariantCardActionPrice
            class="text-gray-50"
            :price="prices.original"
            :is-metered="prices.actual.isMetered"
            :is-discounted="false"
          />
        </template>
      </div>

      <AddToCartButton
        :variant="variant"
        :quantity="totStudents"
        class="[--ks-primary:theme(colors.yellow.40)] [--ks-primarytext:theme(colors.black.80)] [--ks-secondary:theme(colors.yellow.30)]"
        :aria-label="`Bestill. ${label}`"
        size="small"
      >
        Bestill
      </AddToCartButton>
    </div>

    <span v-if="prices.discounted" class="rounded-md border border-yellow-pop bg-yellow-10 p-xs text-gray-40">
      <span class="align-top" aria-hidden="true">*&nbsp;</span>
      <span v-text="disclaimer" />
    </span>

    <PriceLoginCta
      :prices="prices"
      :priceable="variant"
      singular-price
      class="text-base text-gray-40"
    />
  </div>
</template>
